import { Injectable } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

export const authCodeFlowConfig: AuthConfig = {
  issuer: environment.auth.issuer,
  redirectUri: environment.auth.redirectUri,
  clientId: environment.auth.clientId,
  responseType: environment.auth.responseType,
  scope: environment.auth.scope,
  showDebugInformation: environment.auth.showDebugInformation,
  // Prefer refresh_token grant instead of iframe silent refresh to avoid nonce/state warnings
  silentRefreshRedirectUri: undefined,
  useSilentRefresh: false,
  customQueryParams: {
    audience: environment.auth.audience,
  },
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private postLoginNavigated = false;

  constructor(
    private oauth: OAuthService,
    private router: Router,
  ) {
    this.oauth.configure(authCodeFlowConfig);
    this.oauth.loadDiscoveryDocumentAndTryLogin();
    // Use refresh tokens if available (Auth0 allows rotating refresh tokens). This avoids nonce mismatch.
    this.oauth.setupAutomaticSilentRefresh({ disableIFrame: true });

    // After tokens are received/refreshed, navigate back to intended URL if present
    this.oauth.events.subscribe(e => {
      const t = (e && (e as any).type) || '';
      // Only navigate once on initial token reception.
      // Navigating on token_refreshed can override the intended redirect and cause flicker.
      if (t === 'token_received' && !this.postLoginNavigated) {
        this.postLoginNavigated = true;
        this.navigateToPostLoginTarget();
      }
    });
  }

  login(): void {
    this.oauth.initLoginFlow();
  }

  signup(): void {
    this.oauth.initLoginFlow();
  }

  logOut(): void {
    this.oauth.logOut(); 
  }

  getAccessToken(): string {
    return this.oauth.getAccessToken();
  }

  isLoggedIn(): boolean {
    return this.oauth.hasValidAccessToken();
  }

  private navigateToPostLoginTarget(): void {
    try {
      const target = localStorage.getItem('postLoginRedirect');
      if (target) {
        localStorage.removeItem('postLoginRedirect');
        // Prefer Angular navigation for SPA route
        if (target.startsWith('/')) {
          this.router.navigateByUrl(target);
        } else {
          // Fallback in case an absolute URL was stored
          window.location.href = target;
        }
        return;
      }
      // No stored redirect; default to PPK Studio (home page)
      this.router.navigateByUrl('/ppk-studio');
    } catch {}
  }

  private hasAnyPermission(perms: string[]): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(this.base64UrlDecode(parts[1]) || '{}');
      const permissions: string[] = Array.isArray(payload?.permissions) ? payload.permissions : [];
      return perms.some(p => permissions.includes(p));
    } catch {
      return false;
    }
  }

  private base64UrlDecode(str: string): string {
    const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    return decodeURIComponent(
      Array.from(binary)
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  }
}
